import { useState } from 'react';
import type { PendingSettlementLines, SettlementView } from '@cuito/types';
import { Button } from '@cuito/ui';
import { beijingDayRange } from '../../api/ledger';
import { settlementsApi, settlementScopeOf } from '../../api/settlements';
import SettlementLinesTable from './SettlementLinesTable';
import SettlementSummaryCards from './SettlementSummaryCards';
import { GENERATE_FAILED_TEXT, settlementErrorText } from './settlementLabels';

/**
 * 生成结算单（UI 规范 554 行：对方社团 + 期间 + **预览**）。
 *
 * ## 不做展会选择器（A-P7-51）
 *
 * 冻结 testid 表里的 `settlement-generate-event` **本期不渲染**：库里没有可选的 `Event`
 * （`Event` 实体属 Phase 8），摆一个选不出东西的下拉就是死控件。`eventId` 模式的接口
 * 已由 Task 7/8 交付并被 e2e 覆盖 —— UI 只走**期间模式**。
 *
 * ## 预览与提交用同一个口径
 *
 * 两者都由 `settlementScopeOf()` 拼范围（§15.3 的判据是「同范围的两次调用四个金额逐个相等」）——
 * 预览走 `pendingLines`、提交走 `generate`，只有这一点不同。
 *
 * ## 期间是**北京日**
 *
 * `<input type="date">` 给的是北京日历日，而契约要 ISO8601 时刻 ⇒ 复用本仓唯一的
 * 「北京日 → ISO 闭区间」实现 `api/ledger.ts` 的 `beijingDayRange()`（与账本筛选、结算日期同源）。
 * 直接把 `2026-07-01` 发出去会被服务端按 **UTC** 零点解释，而服务端自己的展会模式/反向单
 * 用的是**北京日**区间（`beijingDayRange`）⇒ 同一天在两种模式下会差 8 小时。
 * 只填一个日期时**两个都不发**：让服务端给 422 `SETTLEMENT_SCOPE_REQUIRED`，而不是前端猜一个半截范围。
 *
 * **空态是 `暂无待结算明细`**（预览返回 0 行时由 `SettlementLinesTable` 渲染）。
 */
interface Props {
  counterparties: Array<{ id: string; name: string }>;
  onClose: () => void;
  onGenerated: (view: SettlementView) => void;
}

export default function GenerateSettlementDialog({ counterparties, onClose, onGenerated }: Props) {
  const [counterpartyTenantId, setCounterpartyTenantId] = useState('');
  const [periodStartDay, setPeriodStartDay] = useState('');
  const [periodEndDay, setPeriodEndDay] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<PendingSettlementLines | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const scope = (): ReturnType<typeof settlementScopeOf> => {
    const start = beijingDayRange(periodStartDay);
    const end = beijingDayRange(periodEndDay);
    return settlementScopeOf({
      eventId: '',
      periodStart: start === null ? '' : start.from,
      periodEnd: end === null ? '' : end.to,
    });
  };

  const runPreview = async (): Promise<void> => {
    if (counterpartyTenantId === '') return;
    setPreviewLoading(true);
    setError(null);
    try {
      setPreview(await settlementsApi.pendingLines({ counterpartyTenantId, ...scope() }));
    } catch (e) {
      setPreview(null);
      setError(settlementErrorText(e, GENERATE_FAILED_TEXT));
    } finally {
      setPreviewLoading(false);
    }
  };

  const submit = async (): Promise<void> => {
    if (counterpartyTenantId === '' || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await settlementsApi.generate({
        counterpartyTenantId,
        ...scope(),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      });
      onGenerated(created);
    } catch (e) {
      setError(settlementErrorText(e, GENERATE_FAILED_TEXT));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        role="dialog"
        data-testid="settlement-generate-dialog"
        className="max-h-[90vh] w-[min(880px,92vw)] overflow-auto rounded-lg border-2 border-black bg-white p-5"
      >
        <h2 className="mb-4 text-xl font-bold">生成结算单</h2>

        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">对方社团（收款方 / 货主）</span>
            <select
              data-testid="settlement-generate-counterparty"
              value={counterpartyTenantId}
              onChange={(event) => setCounterpartyTenantId(event.target.value)}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
            >
              <option value="">请选择对方社团</option>
              {counterparties.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">期间开始</span>
            <input
              type="date"
              data-testid="settlement-generate-period-start"
              value={periodStartDay}
              onChange={(event) => setPeriodStartDay(event.target.value)}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">期间结束</span>
            <input
              type="date"
              data-testid="settlement-generate-period-end"
              value={periodEndDay}
              onChange={(event) => setPeriodEndDay(event.target.value)}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">备注</span>
            {/* 备注框**不挂 testid**：冻结表里没有 `settlement-generate-note`（**待裁决 D-21**） */}
            <input
              type="text"
              data-field="note"
              value={note}
              maxLength={200}
              onChange={(event) => setNote(event.target.value)}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
            />
          </label>
        </div>

        {error !== null && (
          <p
            data-testid="settlement-generate-error"
            className="mb-3 rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600"
          >
            {error}
          </p>
        )}

        {preview !== null && (
          <section className="mb-4">
            <SettlementSummaryCards
              totalSales={preview.totalSales}
              refundDeduction={preview.refundDeduction}
              commission={preview.commission}
              netSettlement={preview.netSettlement}
            />
            <SettlementLinesTable lines={preview.lines} loading={false} error={null} />
          </section>
        )}

        <div className="flex justify-end gap-2">
          <Button data-testid="settlement-generate-preview" variant="secondary" onClick={() => void runPreview()}>
            {previewLoading ? '加载中…' : '预览'}
          </Button>
          <Button data-testid="settlement-generate-submit" onClick={() => void submit()} disabled={submitting}>
            生成结算单
          </Button>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
        </div>
      </div>
    </div>
  );
}
